import type { Pt } from '../model/geometry.ts';
import { fitStroke, type EdgeField } from '../model/strokeFit.ts';
import type { Vec2 } from '../model/types.ts';
import type { PencilMode } from '../state/settings.ts';
import { clampToImage, hitMidpoint, hitRun, hitVertex, nearImage, snapPoint, snapPointOriginal, type RunPoints, type SnapFn, type View } from './hitTest.ts';

/**
 * Canvas interaction decisions extracted from the original `features/editor/CanvasView.tsx`
 * (gesture worklets + tap / long-press / freehand handlers) as pure functions. They return
 * what the canvas should do; the caller dispatches the store action and plays haptics.
 */

/** Screen-space hit radii (px): generous for fingers, tighter for mouse and Pencil. */
export const HIT_TOUCH = 24;
export const HIT_FINE = 14;
export const SNAP = 14;
/** Midpoint handles use 0.8 × the hit radius; run selection 0.9 ×. */
export const MIDPOINT_RADIUS_FACTOR = 0.8;
export const RUN_RADIUS_FACTOR = 0.9;
export const LOUPE = 132;
/** Drag previews reach the store at most every 80 ms. */
export const PREVIEW_MS = 80;
/** Freehand: screen px between recorded stroke points, shortest stroke kept, how far it may snap. */
export const STROKE_STEP = 2.5;
export const STROKE_MIN = 16;
export const STROKE_SNAP = 28;
export const FREEHAND_NO_EDGE_TOAST = 'No clear roof edge there, so the line was kept as drawn.';

export type PointerKind = 'touch' | 'stylus' | 'mouse' | 'other';

export function hitRadius(pointer: PointerKind): number {
  return pointer === 'touch' ? HIT_TOUCH : HIT_FINE;
}

/**
 * Pencil-only mode: fingers navigate and select, the stylus edits. Returns whether the pointer
 * may edit and the updated `pencilSeen` flag (set by the first stylus touch/hover).
 */
export function canEdit(pointer: PointerKind, mode: PencilMode, pencilSeen: boolean): { canEdit: boolean; pencilSeen: boolean } {
  const stylus = pointer === 'stylus';
  const seen = pencilSeen || stylus;
  const pencilOnly = mode === 'pencil' || (mode === 'auto' && seen);
  return { canEdit: !pencilOnly || stylus, pencilSeen: seen };
}

/** Image px → stored normalized point, clamped to [0, 1]. */
export function normalizePoint(x: number, y: number, W: number, H: number): Vec2 {
  return { x: Math.max(0, Math.min(1, x / W)), y: Math.max(0, Math.min(1, y / H)) };
}

/** Freehand fit options for a W-px-wide image at view scale `s`. */
export function freehandFitOptions(W: number, s: number): { radius: number; tolerance: number } {
  return {
    radius: Math.min(W * 0.04, Math.max(W * 0.004, STROKE_SNAP / s)),
    tolerance: Math.min(W * 0.008, Math.max(W * 0.0012, 2.5 / s)),
  };
}

/** Neighbour used as the angle-snap anchor while dragging vertex i of an n-point run (−1 = none). */
export function dragAnchorIndex(i: number, n: number): number {
  return i > 0 ? i - 1 : i + 1 < n ? i + 1 : -1;
}

/* ------------------------------------------------------------------------- taps */

export interface CanvasHitInput {
  /** All runs, flat image px, project order. */
  runs: RunPoints;
  /** Index of activeRunId ?? selectedRunId, −1 if none. */
  focus: number;
  /** A run is being drawn (activeRunId set and found). */
  drawing: boolean;
  view: View;
  /** Screen point. */
  sx: number;
  sy: number;
  pointer: PointerKind;
  /** Result of `canEdit` for this pointer. */
  editing: boolean;
  /** Image size (px). */
  W: number;
  H: number;
}

export type TapResult =
  | { type: 'insertPoint'; run: number; index: number; p: Vec2 }
  | { type: 'finishRun' }
  | { type: 'addPoint'; p: Vec2 }
  | { type: 'selectRun'; run: number }
  | { type: 'none' };

/** `onLongPress`: delete the vertex under the pointer (any run when nothing is focused). */
export function resolveLongPress(input: CanvasHitInput): { type: 'removePoint'; run: number; index: number } | { type: 'none' } {
  if (!input.editing) return { type: 'none' };
  const hv = hitVertex(input.runs, input.focus, true, input.sx, input.sy, input.view, hitRadius(input.pointer));
  return hv ? { type: 'removePoint', run: hv[0], index: hv[1] } : { type: 'none' };
}

/* ------------------------------------------------------------------------ drags */

export type DragStart =
  | {
      type: 'vertex';
      run: number;
      index: number;
      /** True when a midpoint handle was grabbed: the point is inserted (`insertPoint`, one undo step). */
      insert: boolean;
      x: number;
      y: number;
      /** Live flat points of the dragged run (with the inserted point). */
      live: number[];
    }
  | { type: 'view' };

/** Single-pointer pan start (not freehand): grab a vertex or midpoint handle, else pan the view. */
export function resolveDragStart(input: CanvasHitInput): DragStart {
  if (!input.editing) return { type: 'view' };
  const { runs, focus, view: v, sx, sy } = input;
  const r = hitRadius(input.pointer);
  const hv = hitVertex(runs, focus, true, sx, sy, v, r);
  const hm = hv ? null : hitMidpoint(runs, focus, input.drawing, sx, sy, v, r * MIDPOINT_RADIUS_FACTOR);
  if (!hv && !hm) return { type: 'view' };
  const run = hv ? hv[0] : hm![0];
  const i = hv ? hv[1] : hm![1];
  const pts = runs[run].slice();
  const x = hv ? pts[i * 2] : hm![2];
  const y = hv ? pts[i * 2 + 1] : hm![3];
  if (hm) pts.splice(i * 2, 0, x, y);
  return { type: 'vertex', run, index: i, insert: !!hm, x, y, live: pts };
}

/* --------------------------------------------------------------------- freehand */

/** Whether a new stroke point (image px) is far enough from the last one to record. */
export function shouldRecordStrokePoint(last: Pt, next: Pt, s: number): boolean {
  return Math.hypot(next.x - last.x, next.y - last.y) * s >= STROKE_STEP;
}

export type FreehandResult =
  | { type: 'discard' }
  | {
      type: 'addRun';
      /** Normalized, clamped points for `addRun`. */
      points: Vec2[];
      snapped: boolean;
      /** Toast to show, or null. */
      toast: string | null;
    };

/**
 * The snapping-dependent decisions, built over a snap function. The default exports use the
 * FIXED nearest-endpoint `snapPoint`; `@better-lights/core/original` builds them over
 * `snapPointOriginal` (first endpoint in project order).
 */
export function createInteraction(snapPoint: SnapFn) {
  /** `onTap` in CanvasView. `selectRun` with run −1 means deselect (`selectRun(null)`). */
  function resolveTap(input: CanvasHitInput & { tool: 'draw' | 'freehand' | 'select' }): TapResult {
    const { runs, focus, view: v, sx, sy, W, H } = input;
    const r = hitRadius(input.pointer);
    if (input.editing) {
      const hm = hitMidpoint(runs, focus, input.drawing, sx, sy, v, r * MIDPOINT_RADIUS_FACTOR);
      if (hm) return { type: 'insertPoint', run: hm[0], index: hm[1], p: normalizePoint(hm[2], hm[3], W, H) };
    }

    if (input.editing && input.tool === 'draw') {
      const active = input.drawing ? runs[focus] : null;
      const hv = hitVertex(runs, focus, false, sx, sy, v, r);
      if (active && hv && hv[1] === active.length / 2 - 1 && active.length >= 4) return { type: 'finishRun' };
      const ix = (sx - v.tx) / v.s;
      const iy = (sy - v.ty) / v.s;
      if (!nearImage(ix, iy, W, H)) return { type: 'none' };
      const [cx, cy] = clampToImage(ix, iy, W, H);
      const anchorX = active && active.length >= 2 ? active[active.length - 2] : null;
      const anchorY = active && active.length >= 2 ? active[active.length - 1] : null;
      const [x, y] = snapPoint(runs, active ? focus : -1, cx, cy, anchorX, anchorY, v, SNAP);
      return { type: 'addPoint', p: normalizePoint(x, y, W, H) };
    }

    if (hitVertex(runs, focus, false, sx, sy, v, r)) return { type: 'none' };
    return { type: 'selectRun', run: hitRun(runs, sx, sy, v, r * RUN_RADIUS_FACTOR) };
  }

  /**
   * Pan update while dragging vertex `index` of run `run`: clamp to the image, then snap
   * (other runs' endpoints from the store's `runs`; angle relative to the neighbour in `live`).
   */
  function dragMove(
    runs: RunPoints,
    run: number,
    index: number,
    live: number[],
    sx: number,
    sy: number,
    v: View,
    W: number,
    H: number,
  ): { x: number; y: number; snapped: boolean } {
    const n = live.length / 2;
    const [cx, cy] = clampToImage((sx - v.tx) / v.s, (sy - v.ty) / v.s, W, H);
    const ni = dragAnchorIndex(index, n);
    const [x, y, snapped] = snapPoint(runs, run, cx, cy, ni >= 0 ? live[ni * 2] : null, ni >= 0 ? live[ni * 2 + 1] : null, v, SNAP);
    return { x, y, snapped };
  }

  /** Hover ghost point (Draw tool): null when the pointer is far outside the image. */
  function hoverPoint(
    runs: RunPoints,
    focus: number,
    drawing: boolean,
    live: number[],
    sx: number,
    sy: number,
    v: View,
    W: number,
    H: number,
  ): { x: number; y: number } | null {
    const anchorX = drawing && live.length >= 2 ? live[live.length - 2] : null;
    const anchorY = drawing && live.length >= 2 ? live[live.length - 1] : null;
    const ix = (sx - v.tx) / v.s;
    const iy = (sy - v.ty) / v.s;
    if (!nearImage(ix, iy, W, H)) return null;
    const [cx, cy] = clampToImage(ix, iy, W, H);
    const [x, y] = snapPoint(runs, drawing ? focus : -1, cx, cy, anchorX, anchorY, v, SNAP);
    return { x, y };
  }

  /**
   * `commitStroke`: fit a finished stroke (image px) onto edges, join its ends to nearby run
   * endpoints and normalize. `field` is null when freehand snapping is off (or no field).
   */
  function finishFreehandStroke(
    stroke: Pt[],
    s: number,
    v: View,
    runs: RunPoints,
    W: number,
    H: number,
    field: EdgeField | null,
    snapOn: boolean,
  ): FreehandResult {
    let len = 0;
    for (let i = 1; i < stroke.length; i++) len += Math.hypot(stroke[i].x - stroke[i - 1].x, stroke[i].y - stroke[i - 1].y);
    if (len * s < STROKE_MIN) return { type: 'discard' };
    const { points, snapped } = fitStroke(stroke, snapOn ? field : null, freehandFitOptions(W, s));
    // Join the ends to nearby runs so corners meet exactly, like tapped points do.
    const ends = [0, points.length - 1];
    const out = points.slice();
    for (const i of ends) {
      const [x, y] = snapPoint(runs, -1, out[i].x, out[i].y, null, null, v, SNAP);
      out[i] = { x, y };
    }
    return {
      type: 'addRun',
      points: out.map((p) => normalizePoint(p.x, p.y, W, H)),
      snapped,
      toast: snapOn && !snapped ? FREEHAND_NO_EDGE_TOAST : null,
    };
  }

  return { resolveTap, dragMove, hoverPoint, finishFreehandStroke };
}

export const { resolveTap, dragMove, hoverPoint, finishFreehandStroke } = createInteraction(snapPoint);

/** Original first-match snapping (cross-check against the original app). */
export const originalInteraction = createInteraction(snapPointOriginal);
